import { createColumnHelper } from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { CheckCircle2, PencilIcon, Repeat, StickyNote, Trash2Icon, Undo2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  ScrollableCellWithAvatar,
  ScrollableCellWithColorDot,
} from '@/components/data-table/cells';
import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import i18n from '@/core/i18n';
import { formatTrimmedDecimal } from '@/helpers/finance';
import { cn } from '@/lib/utils';
import { StatusBadge, StatusBadgeType } from '@/modules/common';
import {
  getOverheadStatusText,
  getOverheadTypeText,
} from '@/modules/common/components/molecules/badge/status-configs/overhead-status-config';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';
import { AuthenticatedRoutes } from '@/router/routes';

import { INVOICES_FILTER_KEY_SEARCH } from '../../invoices/constants/invoices-filter-keys';
import { OverheadListItem, OverheadStatus, OverheadType } from '../types/overhead';
import { getOverheadTypeIcon } from './overhead-type-icon-map';

const FORMAT_DATE_SHORT_MONTH_YEAR = 'd MMM, yyyy';

const AMOUNT_DISPLAY_DECIMAL_PLACES = 2;

const columnHelper = createColumnHelper<OverheadListItem>();

const isInvoiced = (overhead: OverheadListItem): boolean =>
  overhead.status === OverheadStatus.Invoiced;

export const getOverheadsTableColumns = ({
  onEdit,
  onDelete,
  onToggleStatus,
  isStatusUpdating,
}: {
  onEdit?: (overhead: OverheadListItem) => void;
  onDelete?: (overhead: OverheadListItem) => void;
  onToggleStatus?: (overhead: OverheadListItem) => void;
  isStatusUpdating?: (overheadId: number) => boolean;
}) => [
  columnHelper.accessor('name', {
    header: i18n.t('authenticated.overheads:headers.name'),
    enableHiding: false,
    minSize: 240,
    size: 320,
    maxSize: 500,
    cell: ({ getValue, row }) => (
      <div className="flex min-w-0 items-center gap-2">
        <div className="min-w-0 flex-1 overflow-hidden">
          <ScrollableText className="whitespace-nowrap">{getValue()}</ScrollableText>
        </div>
        {row.original.seriesId !== null && (
          <SimpleTooltip content={i18n.t('authenticated.overheads:table.recurringTooltip')}>
            <Repeat size={14} className="shrink-0 text-muted-foreground" />
          </SimpleTooltip>
        )}
        {row.original.notes && (
          <SimpleTooltip content={<p className="max-w-64">{row.original.notes}</p>}>
            <StickyNote size={14} className="shrink-0 text-muted-foreground" />
          </SimpleTooltip>
        )}
      </div>
    ),
  }),
  columnHelper.accessor(row => row.project.name, {
    id: 'project',
    header: i18n.t('authenticated.overheads:headers.project'),
    enableHiding: false,
    minSize: 200,
    size: 220,
    maxSize: 400,
    cell: ({ getValue, row }) => (
      <ScrollableCellWithColorDot
        text={getValue()}
        image={row.original.project.logoUrl ?? undefined}
        avatarName={getValue()}
        avatarSize="sm"
        alt={`${getValue()} logo`}
        dotColor={row.original.project.colorHex || 'hsl(var(--muted-foreground))'}
      />
    ),
  }),
  columnHelper.accessor('clientName', {
    header: i18n.t('authenticated.overheads:headers.client'),
    minSize: 150,
    size: 180,
    maxSize: 300,
    cell: ({ getValue }) => {
      const clientName = getValue();
      return clientName ? (
        <ScrollableCellWithAvatar text={clientName} avatarName={clientName} avatarSize="sm" />
      ) : (
        <span className="text-muted-foreground">{i18n.t('common:table.noValue')}</span>
      );
    },
  }),
  columnHelper.accessor('type', {
    header: () => (
      <div className="flex w-full justify-center">
        {i18n.t('authenticated.overheads:headers.type')}
      </div>
    ),
    minSize: 100,
    size: 120,
    maxSize: 180,
    meta: { cellClassName: 'text-center' },
    cell: ({ getValue }) => {
      const TypeIcon = getOverheadTypeIcon(getValue());
      return (
        <StatusBadge
          type={StatusBadgeType.Overhead}
          status={getValue()}
          label={
            <span className="inline-flex items-center gap-1">
              <TypeIcon size={12} className="shrink-0" />
              {getOverheadTypeText(getValue())}
            </span>
          }
        />
      );
    },
  }),
  columnHelper.accessor('date', {
    header: i18n.t('authenticated.overheads:headers.date'),
    minSize: 110,
    size: 130,
    cell: ({ getValue }) => (
      <span className="whitespace-nowrap">
        {format(parseISO(getValue()), FORMAT_DATE_SHORT_MONTH_YEAR)}
      </span>
    ),
  }),
  columnHelper.accessor('amount', {
    header: i18n.t('authenticated.overheads:headers.amount'),
    enableHiding: false,
    minSize: 110,
    size: 140,
    cell: ({ getValue, row }) => {
      const amount = getValue();
      return (
        <span className={cn('whitespace-nowrap', amount < 0 && 'text-destructive')}>
          {formatTrimmedDecimal(amount, AMOUNT_DISPLAY_DECIMAL_PLACES)}{' '}
          <span className="text-muted-foreground">{row.original.currencyCode}</span>
        </span>
      );
    },
  }),
  columnHelper.accessor('status', {
    header: () => (
      <div className="flex w-full justify-center">
        {i18n.t('authenticated.overheads:headers.status')}
      </div>
    ),
    enableHiding: false,
    minSize: 100,
    size: 130,
    maxSize: 180,
    meta: { cellClassName: 'text-center' },
    cell: ({ getValue, row }) => {
      const status = getValue();
      const paidAt = row.original.paidAt;
      return (
        <StatusBadge
          type={StatusBadgeType.Overhead}
          status={status}
          label={getOverheadStatusText(status)}
          infoTooltip={
            status === OverheadStatus.Paid && paidAt
              ? i18n.t('authenticated.overheads:table.paidOn', {
                  date: format(parseISO(paidAt), FORMAT_DATE_SHORT_MONTH_YEAR),
                })
              : undefined
          }
        />
      );
    },
  }),
  columnHelper.display({
    id: 'invoice',
    header: i18n.t('authenticated.overheads:headers.invoice'),
    minSize: 140,
    size: 160,
    cell: ({ row }) => {
      const { invoice, type } = row.original;
      const noValue = i18n.t('common:table.noValue');

      if (type !== OverheadType.Income || !invoice?.number) {
        return <span className="text-muted-foreground">{noValue}</span>;
      }

      return (
        <Link
          to={{
            pathname: AuthenticatedRoutes.Invoices,
            search: `?${INVOICES_FILTER_KEY_SEARCH}=${encodeURIComponent(invoice.number)}`,
          }}
          onClick={e => e.stopPropagation()}
          className="whitespace-nowrap text-primary underline-offset-2 hover:underline"
        >
          {i18n.t('authenticated.overheads:table.invoiceLink', { number: invoice.number })}
        </Link>
      );
    },
  }),
  columnHelper.display({
    id: 'action',
    header: () => (
      <div className="flex w-full justify-center">
        {i18n.t('authenticated.overheads:headers.action')}
      </div>
    ),
    enableHiding: false,
    meta: { cellClassName: 'text-center' },
    size: 120,
    cell: ({ row }) => {
      const overhead = row.original;
      const invoiced = isInvoiced(overhead);
      const lockedTooltip = i18n.t('authenticated.overheads:table.invoicedLockedTooltip', {
        number: overhead.invoice?.number ?? '',
      });

      return (
        <div className="flex items-center justify-center">
          {onToggleStatus && overhead.type === OverheadType.Cost && (
            <Button
              size="icon"
              variant="ghost"
              onClick={e => {
                e.stopPropagation();
                onToggleStatus(overhead);
              }}
              disabled={isStatusUpdating?.(overhead.id)}
              title={
                overhead.status === OverheadStatus.Planned
                  ? i18n.t('authenticated.overheads:table.markPaid')
                  : i18n.t('authenticated.overheads:table.markPlanned')
              }
            >
              {overhead.status === OverheadStatus.Planned ? (
                <CheckCircle2 size={16} />
              ) : (
                <Undo2 size={16} />
              )}
            </Button>
          )}
          {onEdit && (
            <Button
              size="icon"
              variant="ghost"
              onClick={e => {
                e.stopPropagation();
                onEdit(overhead);
              }}
              disabled={invoiced}
              title={
                invoiced ? lockedTooltip : i18n.t('authenticated.overheads:table.editOverhead')
              }
            >
              <PencilIcon size={16} />
            </Button>
          )}
          {onDelete && (
            <Button
              size="icon"
              variant="ghost"
              onClick={e => {
                e.stopPropagation();
                onDelete(overhead);
              }}
              disabled={invoiced}
              title={
                invoiced ? lockedTooltip : i18n.t('authenticated.overheads:table.deleteOverhead')
              }
            >
              <Trash2Icon size={16} />
            </Button>
          )}
        </div>
      );
    },
  }),
];
